/**
 * vo-bfcm-bar · BFCM 2026 announcement bar (CTO, 2026-11-03).
 * Phase + market + countdown, all from fixed instants (no cookie, same for everyone).
 * Markup and server-side first paint come from blocks/_vo-bfcm.liquid; this only re-evaluates on the client
 * so the bar is right at a boundary even on a cached page.
 *
 * Phases: pre (before A), A, B, X (after the VIP slide stops, before C), C, D, E (after END).
 * Each slide declares where it shows: data-show-au / data-show-nz / data-show-other = space-separated phases.
 */
class VoBfcmBar extends HTMLElement {
  connectedCallback() {
    this.dataset.ready = 'true';
    this.t = this.dataset.t.split(',').map(Number); // A, B, C, D, END, BEND (epoch seconds)
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.interval = (parseInt(this.dataset.interval, 10) || 6) * 1000;
    this.index = 0;
    this.paused = false;
    this.preview = this.dataset.preview === 'true'; // set by Liquid only on non-live themes
    this.lastRotate = Date.now();

    if (this.preview) {
      const q = new URLSearchParams(location.search);
      if (q.get('bfcm_now')) this.fakeNow = Number(q.get('bfcm_now'));
      if (q.get('bfcm_phase')) this.fakePhase = q.get('bfcm_phase').toUpperCase();
      if (q.get('bfcm_market')) this.fakeMarket = q.get('bfcm_market').toUpperCase();
    }

    this.addEventListener('mouseenter', () => (this.paused = true));
    this.addEventListener('mouseleave', () => (this.paused = false));
    this.addEventListener('focusin', () => (this.paused = true));
    this.addEventListener('focusout', () => (this.paused = false));

    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  now() {
    if (this.fakeNow) return this.fakeNow + Math.floor((Date.now() - this.started) / 1000);
    return Math.floor(Date.now() / 1000);
  }

  phase(n) {
    if (this.fakePhase) return this.fakePhase;
    const [a, b, c, d, end, bend] = this.t;
    if (n < a) return 'PRE';
    if (n < b) return 'A';
    if (bend && n >= bend && n < c) return 'X'; // VIP slide has stopped, public sale not started: normal bar
    if (n < c) return 'B';
    if (n < d) return 'C';
    if (n < end) return 'D';
    return 'E';
  }

  market() {
    return (this.fakeMarket || this.dataset.market || 'OTHER').toUpperCase();
  }

  static clock(seconds, withSeconds, label) {
    const s = Math.max(0, seconds);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (v) => String(v).padStart(2, '0');
    const units = [];
    if (d > 0) units.push([pad(d), d === 1 ? 'day' : 'days']);
    if (d > 0 || h > 0 || withSeconds) units.push([pad(h), 'hrs']);
    units.push([pad(m), 'min']);
    if (withSeconds) units.push([pad(sec), 'sec']);
    const boxes = units
      .map(([v, l]) => `<span class="vo-bfcm__unit"><span class="vo-bfcm__digits">${v}</span><span class="vo-bfcm__unit-label">${l}</span></span>`)
      .join('<span class="vo-bfcm__sep" aria-hidden="true">:</span>');
    const text = units.map(([v, l]) => `${Number(v)} ${l}`).join(' ');
    return `${label ? `<span class="vo-bfcm__label">${label}</span>` : ''}${boxes}<span class="visually-hidden">${label} ${text}</span>`;
  }

  tick() {
    if (this.fakeNow && !this.started) this.started = Date.now();
    const n = this.now();
    const phase = this.phase(n);
    const market = this.market().toLowerCase();
    const key = market === 'au' || market === 'nz' ? market : 'other';
    const [, b, c, , end] = this.t;
    const target = { b, c, end };

    // Which slides belong to this phase + market.
    const attr = `show${key[0].toUpperCase()}${key.slice(1)}`;
    const matches = (el) => (el.dataset[attr] || '').toUpperCase().split(' ').includes(phase);
    let eligible = this.slides.filter(matches);
    // Never an empty bar: if every sale slide is switched off or sold out, fall back to the normal bar.
    if (!eligible.length) eligible = this.slides.filter((el) => el.dataset.slide === 'normal');
    const sig = `${phase}|${key}|${eligible.map((e) => e.dataset.slide).join(',')}`;
    if (sig !== this.sig) {
      this.sig = sig;
      this.eligible = eligible;
      this.index = 0;
      this.lastRotate = Date.now();
      this.slides.forEach((el) => (el.hidden = !eligible.includes(el)));
      if (this.eligible.length) this.show(0);
    }

    // Clock-style countdown: boxed days / hours / minutes (+ seconds on the last day only).
    this.querySelectorAll('[data-countdown]').forEach((el) => {
      const left = target[el.dataset.countdown] - n;
      if (left <= 0) return; // next tick moves the phase on
      const html = VoBfcmBar.clock(left, phase === 'D', el.dataset.label || '');
      if (el.dataset.rendered !== html) {
        el.dataset.rendered = html;
        el.innerHTML = html;
      }
    });

    const multi = this.eligible.length > 1;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (multi && !reduced && !this.paused && !document.hidden && Date.now() - this.lastRotate >= this.interval) {
      this.step(1);
    }
  }

  step(dir) {
    if (this.eligible.length < 2) return;
    this.show((this.index + dir + this.eligible.length) % this.eligible.length);
  }

  show(i) {
    this.index = i;
    this.lastRotate = Date.now();
    this.eligible.forEach((el, k) => {
      el.setAttribute('aria-hidden', String(k !== i));
      el.classList.toggle('is-active', k === i);
    });
  }
}

if (!customElements.get('vo-bfcm-bar')) customElements.define('vo-bfcm-bar', VoBfcmBar);
